import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { MessageSquare, Send, Phone, Users, Check, AlertCircle, Settings, TestTube, Plus, Pencil, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SMSPage = () => {
  const [smsStatus, setSmsStatus] = useState({ configured: false, phone_number: null });
  const [galleries, setGalleries] = useState([]);
  const [selectedGalleries, setSelectedGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [sentResults, setSentResults] = useState(null);
  
  // Template management
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateText, setTemplateText] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  useEffect(() => {
    fetchSMSStatus();
    fetchGalleries();
    fetchTemplates();
  }, []);

  const fetchSMSStatus = async () => {
    try {
      const response = await axios.get(`${API}/sms/status`);
      setSmsStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch SMS status:', error);
    }
  };

  const fetchGalleries = async () => {
    try {
      const response = await axios.get(`${API}/galleries?status=published`);
      setGalleries(response.data);
    } catch (error) {
      toast.error('Failed to load galleries');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/sms/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const toggleGallery = (galleryId) => {
    setSelectedGalleries(prev =>
      prev.includes(galleryId)
        ? prev.filter(id => id !== galleryId)
        : [...prev, galleryId]
    );
  };

  const selectAll = () => {
    const galleriesWithPhone = galleries.filter(g => g.client_phone);
    setSelectedGalleries(galleriesWithPhone.map(g => g.id));
  };

  const deselectAll = () => {
    setSelectedGalleries([]);
  };

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setCustomMessage(template.template);
    }
  };

  const openNewTemplateDialog = () => {
    setEditingTemplate(null);
    setTemplateName('');
    setTemplateText('');
    setShowTemplateDialog(true);
  };

  const openEditTemplateDialog = (template) => {
    setEditingTemplate(template);
    setTemplateName(template.name);
    setTemplateText(template.template);
    setShowTemplateDialog(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim() || !templateText.trim()) {
      toast.error('Please enter a name and message');
      return;
    }

    setSavingTemplate(true);
    try {
      if (editingTemplate) {
        await axios.put(`${API}/sms/templates/${editingTemplate.id}`, {
          name: templateName,
          template: templateText
        });
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/sms/templates`, {
          name: templateName,
          template: templateText
        });
        toast.success('Template created');
      }
      setShowTemplateDialog(false);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${API}/sms/templates/${templateId}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const generatePreview = (gallery) => {
    return customMessage
      .replace(/{client_name}/g, gallery.client_name || 'Customer')
      .replace(/{gallery_name}/g, gallery.name || 'your gallery')
      .replace(/{access_code}/g, gallery.access_code || '');
  };

  const handleSendSMS = async () => {
    if (selectedGalleries.length === 0) {
      toast.error('Please select at least one gallery');
      return;
    }

    if (!customMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/sms/send-bulk`, {
        gallery_ids: selectedGalleries,
        message_template: customMessage
      });

      const results = {
        total: response.data.sent.length + response.data.failed.length + response.data.skipped.length,
        sent: response.data.sent.length,
        failed: response.data.failed.length,
        skipped: response.data.skipped.length,
        details: response.data
      };

      setSentResults(results);
      toast.success(`Sent ${results.sent} SMS messages successfully!`);
      setSelectedGalleries([]);
      setShowPreview(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  const handleTestSMS = async () => {
    if (!testPhone.trim()) {
      toast.error('Please enter a phone number');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/sms/test`, { phone: testPhone });
      toast.success('Test SMS sent successfully!');
      setShowTestDialog(false);
      setTestPhone('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test SMS');
    } finally {
      setSending(false);
    }
  };

  const galleriesWithPhone = galleries.filter(g => g.client_phone);
  const galleriesWithoutPhone = galleries.filter(g => !g.client_phone);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading text-stone-800">SMS Notifications</h1>
          <p className="text-stone-600 mt-1">Send text messages to your clients</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowTestDialog(true)}
            disabled={!smsStatus.configured}
            data-testid="test-sms-btn"
          >
            <TestTube className="h-4 w-4 mr-2" />
            Test SMS
          </Button>
          <Button
            className="btn-action"
            onClick={() => setShowPreview(true)}
            disabled={selectedGalleries.length === 0 || !smsStatus.configured}
            data-testid="send-sms-btn"
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            Send SMS ({selectedGalleries.length})
          </Button>
        </div>
      </div>

      {/* SMS Status Card */}
      <Card className={smsStatus.configured ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}>
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            {smsStatus.configured ? (
              <>
                <Check className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">SMS Notifications Enabled</p>
                  <p className="text-sm text-green-700">
                    Sending from number ending in ...{smsStatus.phone_number}
                  </p>
                </div>
              </>
            ) : (
              <>
                <AlertCircle className="h-5 w-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">SMS Not Configured</p>
                  <p className="text-sm text-amber-700">
                    Add Twilio credentials to enable SMS notifications
                  </p>
                </div>
                <Button variant="outline" size="sm" className="ml-auto" disabled>
                  <Settings className="h-4 w-4 mr-2" />
                  Configure
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Message Template */}
      <Card className="border-stone-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading text-xl">Message Template</CardTitle>
              <CardDescription>
                Choose a template or write a custom message. Use {'{client_name}'}, {'{gallery_name}'}, {'{access_code}'} for personalization.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={openNewTemplateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              New Template
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Template</Label>
            <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select a template..." />
              </SelectTrigger>
              <SelectContent>
                {templates.map(template => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name} {template.is_default && <span className="text-stone-400">(Default)</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Show templates list with edit/delete options */}
          {templates.length > 0 && (
            <div className="border rounded-lg divide-y">
              {templates.map(template => (
                <div key={template.id} className="p-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-stone-800 text-sm flex items-center gap-2">
                      <FileText className="h-4 w-4 text-stone-400" />
                      {template.name}
                      {template.is_default && (
                        <Badge variant="outline" className="text-xs">Default</Badge>
                      )}
                    </p>
                    <p className="text-xs text-stone-500 truncate mt-1">{template.template}</p>
                  </div>
                  {!template.is_default && (
                    <div className="flex gap-1 ml-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openEditTemplateDialog(template)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDeleteTemplate(template.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2">
            <Label>Message</Label>
            <Textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Enter your SMS message..."
              rows={4}
              maxLength={160}
              className="font-mono text-sm"
            />
            <p className="text-xs text-stone-500 text-right">
              {customMessage.length}/160 characters
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Select Galleries */}
      <Card className="border-stone-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading text-xl">Select Recipients</CardTitle>
              <CardDescription>Choose which clients should receive the SMS</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAll}>
                Select All
              </Button>
              <Button variant="outline" size="sm" onClick={deselectAll}>
                Deselect All
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <p className="text-stone-500">Loading galleries...</p>
            </div>
          ) : galleriesWithPhone.length > 0 ? (
            <ScrollArea className="h-80">
              <div className="space-y-2">
                <p className="text-sm font-medium text-stone-500 mb-2">
                  <Phone className="h-4 w-4 inline mr-1" />
                  With Phone Number ({galleriesWithPhone.length})
                </p>
                {galleriesWithPhone.map((gallery) => (
                  <div
                    key={gallery.id}
                    className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${
                      selectedGalleries.includes(gallery.id)
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                    onClick={() => toggleGallery(gallery.id)}
                  >
                    <Checkbox
                      checked={selectedGalleries.includes(gallery.id)}
                      onCheckedChange={() => toggleGallery(gallery.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-800 truncate">{gallery.name}</p>
                      <p className="text-sm text-stone-500 truncate">
                        {gallery.client_name} • {gallery.client_phone}
                      </p>
                    </div>
                    <Badge className="bg-green-100 text-green-700">
                      {gallery.status}
                    </Badge>
                  </div>
                ))}

                {galleriesWithoutPhone.length > 0 && (
                  <>
                    <p className="text-sm font-medium text-stone-500 mt-6 mb-2">
                      <AlertCircle className="h-4 w-4 inline mr-1" />
                      Missing Phone Number ({galleriesWithoutPhone.length})
                    </p>
                    {galleriesWithoutPhone.map((gallery) => (
                      <div
                        key={gallery.id}
                        className="flex items-center gap-4 p-4 rounded-lg border border-stone-200 bg-stone-50 opacity-60"
                      >
                        <Checkbox disabled />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-stone-600 truncate">{gallery.name}</p>
                          <p className="text-sm text-stone-400 truncate">
                            {gallery.client_name || 'No client info'} • No phone
                          </p>
                        </div>
                        <Badge className="bg-stone-100 text-stone-500">No Phone</Badge>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </ScrollArea>
          ) : (
            <div className="text-center py-12">
              <Phone className="h-12 w-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">No galleries with phone numbers</p>
              <p className="text-sm text-stone-400 mt-1">Add phone numbers to galleries to send SMS</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Preview SMS</DialogTitle>
            <DialogDescription>
              Review your message before sending to {selectedGalleries.length} recipients
            </DialogDescription>
          </DialogHeader>

          {selectedGalleries.length > 0 && (
            <div className="space-y-4">
              <div className="bg-stone-50 rounded-lg p-4">
                <p className="text-sm font-medium text-stone-600 mb-2">Message Preview:</p>
                <p className="text-stone-800 whitespace-pre-wrap">
                  {generatePreview(galleries.find(g => g.id === selectedGalleries[0]) || {})}
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> This will send SMS to {selectedGalleries.length} clients.
                  Standard messaging rates may apply.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Cancel
            </Button>
            <Button
              className="btn-action"
              onClick={handleSendSMS}
              disabled={sending}
            >
              {sending ? (
                'Sending...'
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send {selectedGalleries.length} SMS
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test SMS Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Send Test SMS</DialogTitle>
            <DialogDescription>
              Send a test message to verify your SMS configuration
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="test-phone">Phone Number</Label>
              <Input
                id="test-phone"
                type="tel"
                placeholder="(555) 123-4567"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
              />
              <p className="text-xs text-stone-500">
                Enter your phone number to receive a test message
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestDialog(false)}>
              Cancel
            </Button>
            <Button
              className="btn-action"
              onClick={handleTestSMS}
              disabled={sending || !testPhone.trim()}
            >
              {sending ? 'Sending...' : 'Send Test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Results Dialog */}
      <Dialog open={!!sentResults} onOpenChange={() => setSentResults(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Check className="h-6 w-6 text-green-600" />
              SMS Sent!
            </DialogTitle>
            <DialogDescription>
              Successfully sent {sentResults?.sent} of {sentResults?.total} messages
            </DialogDescription>
          </DialogHeader>

          {sentResults && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-700">{sentResults.sent}</p>
                  <p className="text-sm text-green-600">Sent</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-red-700">{sentResults.failed}</p>
                  <p className="text-sm text-red-600">Failed</p>
                </div>
                <div className="bg-stone-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-stone-700">{sentResults.skipped}</p>
                  <p className="text-sm text-stone-600">Skipped</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setSentResults(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Create/Edit Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingTemplate ? 'Edit Template' : 'Create New Template'}
            </DialogTitle>
            <DialogDescription>
              Create a reusable SMS message template. Use {'{client_name}'}, {'{gallery_name}'}, {'{access_code}'} for personalization.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name</Label>
              <Input
                id="template-name"
                placeholder="e.g., Spring Soccer Reminder"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-message">Message</Label>
              <Textarea
                id="template-message"
                placeholder="Hi {client_name}! Your photos are ready..."
                value={templateText}
                onChange={(e) => setTemplateText(e.target.value)}
                rows={4}
                maxLength={160}
                className="font-mono text-sm"
              />
              <div className="flex justify-between text-xs text-stone-500">
                <span>Variables: {'{client_name}'}, {'{gallery_name}'}, {'{access_code}'}</span>
                <span>{templateText.length}/160</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>
              Cancel
            </Button>
            <Button
              className="btn-action"
              onClick={handleSaveTemplate}
              disabled={savingTemplate || !templateName.trim() || !templateText.trim()}
            >
              {savingTemplate ? 'Saving...' : editingTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SMSPage;
